/**
 * AR aging — дебиторская задолженность по объектам.
 *
 * Структура:
 *  • Hero-блок: главная сумма AR + просрочка 180+ + собираемость
 *  • 5 KPI-карточек: по бакетам (180+ alarm, 91-180, 31-90, 0-30, без даты)
 *  • Toolbar: регион, поиск, CSV-экспорт
 *  • Группированная таблица: bucket → объекты, прогресс-бар собираемости,
 *    цветная полоска слева, deep-link в AmoCRM.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchArAging, formatRub, OBJECT_STATUS_LABELS,
  type ArAgingBucket, type ArAgingObjectRow, type ArAgingResponse,
} from "@/api/finance";
import { ApiError } from "@/api/client";


const BUCKETS_ORDER: ArAgingBucket[] = ["180+", "91-180", "31-90", "0-30", "no_signed_date"];

const BUCKET_THEME: Record<ArAgingBucket, {
  kpiClass: string;       // "alarm" | "good" | ""
  stripe: string;         // цвет полоски слева в строке
  pillBg: string;         // фон pill-бакета
  pillFg: string;         // текст pill-бакета
  pillBorder: string;     // border pill-бакета
  groupBg: string;        // фон group-header
  groupAccent: string;    // цветная вертикальная полоска у group-header
  shortLabel: string;     // "180+", "91-180" и т.п. (для pill и chip)
}> = {
  "180+":           { kpiClass: "alarm", stripe: "var(--brick)",      pillBg: "var(--brick-50)",  pillFg: "var(--brick-deep)", pillBorder: "var(--brick-100)",     groupBg: "var(--brick-50)",  groupAccent: "var(--brick)",      shortLabel: "180+" },
  "91-180":         { kpiClass: "",      stripe: "#D4923C",            pillBg: "var(--amber-soft)",pillFg: "#7A4E12",           pillBorder: "rgba(201,122,31,0.3)", groupBg: "#FFF1DA",           groupAccent: "var(--amber)",       shortLabel: "91-180" },
  "31-90":          { kpiClass: "",      stripe: "#D4B53C",            pillBg: "#FFF5C2",          pillFg: "#6A5400",           pillBorder: "#E6CC66",              groupBg: "#FFFAE1",           groupAccent: "#C9A93C",           shortLabel: "31-90" },
  "0-30":           { kpiClass: "good",  stripe: "var(--green)",       pillBg: "var(--green-soft)",pillFg: "#1F4F1F",           pillBorder: "rgba(61,122,61,0.3)", groupBg: "var(--green-soft)", groupAccent: "var(--green)",      shortLabel: "0-30" },
  "no_signed_date": { kpiClass: "",      stripe: "var(--mute-2)",      pillBg: "var(--paper)",     pillFg: "var(--mute)",        pillBorder: "var(--line)",          groupBg: "var(--paper-2)",    groupAccent: "var(--mute-2)",     shortLabel: "без даты" },
};


function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}


function downloadCsv(filename: string, rows: ArAgingObjectRow[]) {
  const header = ["Клиент","Адрес","Регион","Акция","Статус","Подписан","Дней","Бакет","Контракт","Получено","AR","Собрано %","AmoCRM"];
  const esc = (v: unknown) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",;\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [header.join(";")];
  for (const o of rows) {
    lines.push([
      esc(o.client_name), esc(o.address), esc(o.region),
      esc(o.is_promotion ? "акция" : ""),
      esc(o.status ? (OBJECT_STATUS_LABELS[o.status] || o.status) : ""),
      esc(o.contract_signed_at || ""),
      esc(o.days_outstanding ?? ""),
      esc(BUCKET_THEME[o.bucket].shortLabel),
      esc(o.contract_sum.toFixed(2)),
      esc(o.cash_received.toFixed(2)),
      esc(o.ar.toFixed(2)),
      esc(o.collected_pct.toFixed(1)),
      esc(o.amo_url || ""),
    ].join(";"));
  }
  // BOM для Excel-русского
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}


function ProgressBar({ pct, tone }: { pct: number; tone: string }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div style={{
      position: "relative",
      height: 6,
      width: 80,
      background: "var(--line-soft)",
      borderRadius: 999,
      overflow: "hidden",
    }}>
      <div style={{
        width: `${w}%`,
        height: "100%",
        background: tone,
        transition: "width 200ms ease",
      }} />
    </div>
  );
}


function BucketKpi({ b, totalAr, isActive, onClick }: {
  b: ArAgingResponse["buckets"][number];
  totalAr: number;
  isActive: boolean;
  onClick: () => void;
}) {
  const th = BUCKET_THEME[b.bucket];
  const share = totalAr > 0 ? (b.ar / totalAr) * 100 : 0;
  const empty = b.objects_count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={empty}
      className={`kpi ${th.kpiClass}`}
      style={{
        textAlign: "left",
        cursor: empty ? "default" : "pointer",
        opacity: empty ? 0.55 : 1,
        outline: isActive ? `2px solid ${th.stripe}` : "none",
        outlineOffset: 2,
        border: "1px solid var(--line)",
        fontFamily: "inherit",
      }}
      title={empty ? "Нет объектов в этом бакете" : `Кликни чтобы отфильтровать ${b.label.toLowerCase()}`}
    >
      <div className="kpi-label">{b.label}</div>
      <div className="kpi-value" style={{ fontSize: 28, whiteSpace: "nowrap" }}>
        {formatRub(b.ar, { unit: true })}
      </div>
      <div className="kpi-foot">
        <span style={{ fontSize: 12, color: "var(--mute)" }}>
          {b.objects_count} {b.objects_count === 1 ? "объект" : (b.objects_count >= 2 && b.objects_count <= 4 ? "объекта" : "объектов")}
        </span>
        {totalAr > 0 && (
          <span style={{ fontSize: 12, color: "var(--mute)" }}>
            доля {share.toFixed(0)}%
          </span>
        )}
      </div>
      {b.contract_sum > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
          <ProgressBar pct={b.collected_pct} tone={th.stripe} />
          <span style={{ fontSize: 11, color: "var(--mute)" }}>
            собрано {b.collected_pct.toFixed(0)}%
          </span>
        </div>
      )}
    </button>
  );
}


function ObjectRow({ o }: { o: ArAgingObjectRow }) {
  const th = BUCKET_THEME[o.bucket];
  return (
    <tr style={{ borderBottom: "1px solid var(--line-soft)" }}
        className="ar-row">
      <td style={{
        position: "relative",
        paddingLeft: 16,
        borderLeft: `4px solid ${th.stripe}`,
        verticalAlign: "top",
      }}>
        <div style={{ fontWeight: 600, color: "var(--ink)", lineHeight: 1.25 }}>
          {o.client_name}
          {o.is_promotion && (
            <span style={{
              marginLeft: 8, padding: "1px 7px", borderRadius: 999,
              background: "rgba(212,167,58,0.15)", color: "#8a6e10",
              border: "1px solid rgba(212,167,58,0.35)",
              fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
            }}>
              акция
            </span>
          )}
        </div>
        {o.address && (
          <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 2, maxWidth: 360 }}>
            {o.address}
          </div>
        )}
      </td>
      <td style={{ color: "var(--ink-3)" }}>{o.region || "—"}</td>
      <td style={{ color: "var(--ink-3)", whiteSpace: "nowrap" }}>{fmtDate(o.contract_signed_at)}</td>
      <td style={{ textAlign: "right", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
        {o.days_outstanding !== null ? (
          <span style={{ fontFamily: "var(--mono)", fontWeight: 500 }}>
            {o.days_outstanding}
          </span>
        ) : "—"}
      </td>
      <td style={{ textAlign: "right", color: "var(--ink-2)", whiteSpace: "nowrap" }}>
        {formatRub(o.contract_sum, { unit: true })}
      </td>
      <td style={{ textAlign: "right", color: "var(--ink-3)", whiteSpace: "nowrap" }}>
        {formatRub(o.cash_received, { unit: true })}
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <span style={{
          fontFamily: "var(--serif)",
          fontWeight: 700,
          fontSize: 16,
          color: o.bucket === "180+" ? "var(--brick)" : "var(--ink)",
          letterSpacing: "-0.01em",
        }}>
          {formatRub(o.ar, { unit: true })}
        </span>
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
          <ProgressBar pct={o.collected_pct} tone={th.stripe} />
          <span style={{ fontSize: 12, color: "var(--mute)", width: 36, textAlign: "right" }}>
            {o.collected_pct.toFixed(0)}%
          </span>
        </div>
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {o.amo_url ? (
          <a className="card-link" href={o.amo_url} target="_blank" rel="noopener noreferrer"
             style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            AmoCRM <Icon name="external" size={11}/>
          </a>
        ) : <span style={{ color: "var(--mute-2)" }}>—</span>}
      </td>
    </tr>
  );
}


function GroupHeader({ bucket, info, collapsed, onToggle }: {
  bucket: ArAgingBucket;
  info: ArAgingResponse["buckets"][number];
  collapsed: boolean;
  onToggle: () => void;
}) {
  const th = BUCKET_THEME[bucket];
  return (
    <tr style={{ position: "sticky", top: 0, zIndex: 1 }}>
      <td colSpan={9} style={{ padding: 0 }}>
        <button
          type="button"
          onClick={onToggle}
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            padding: "10px 14px",
            background: th.groupBg,
            borderTop: `1px solid ${th.pillBorder}`,
            borderBottom: `1px solid ${th.pillBorder}`,
            borderLeft: `4px solid ${th.groupAccent}`,
            cursor: "pointer",
            textAlign: "left",
            font: "inherit",
            color: th.pillFg,
            gap: 12,
          }}
        >
          <span style={{
            display: "inline-flex", justifyContent: "center", width: 14, height: 14,
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 120ms ease",
            fontSize: 10, opacity: 0.6,
          }}>
            ▼
          </span>
          <span style={{
            fontFamily: "var(--serif)", fontWeight: 700, fontSize: 14,
            letterSpacing: "-0.01em",
          }}>
            {info.label}
          </span>
          <span style={{ fontSize: 12, opacity: 0.75 }}>
            {info.objects_count} {info.objects_count === 1 ? "объект" : "объектов"}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, opacity: 0.75 }}>
            контракт {formatRub(info.contract_sum, { unit: true })}
          </span>
          <span style={{ fontSize: 12, opacity: 0.75 }}>
            получено {formatRub(info.cash_received, { unit: true })}
          </span>
          <span style={{
            fontFamily: "var(--serif)", fontWeight: 700, fontSize: 15,
            color: bucket === "180+" ? "var(--brick)" : th.pillFg,
          }}>
            AR {formatRub(info.ar, { unit: true })}
          </span>
        </button>
      </td>
    </tr>
  );
}


export function FinanceArAgingPage() {
  const [regionFilter, setRegionFilter] = useState<"" | "Москва" | "Юг">("");
  const [bucketFilter, setBucketFilter] = useState<"" | ArAgingBucket>("");
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState<Set<ArAgingBucket>>(new Set(["0-30"]));

  const q = useQuery({
    queryKey: ["finance-ar-aging", regionFilter],
    queryFn: () => fetchArAging({ region: regionFilter || undefined }),
    staleTime: 60_000,
  });

  const items = useMemo(() => {
    const all = q.data?.objects || [];
    const s = search.trim().toLowerCase();
    return all.filter(o => {
      if (bucketFilter && o.bucket !== bucketFilter) return false;
      if (s && !(o.client_name?.toLowerCase().includes(s) || (o.address || "").toLowerCase().includes(s))) return false;
      return true;
    });
  }, [q.data, bucketFilter, search]);

  const grouped = useMemo(() => {
    const m = new Map<ArAgingBucket, ArAgingObjectRow[]>();
    for (const o of items) {
      if (!m.has(o.bucket)) m.set(o.bucket, []);
      m.get(o.bucket)!.push(o);
    }
    return m;
  }, [items]);

  if (q.isLoading) return (
    <FinanceLayout>
      <Spinner size="lg" label="Загрузка дебиторки…" />
    </FinanceLayout>
  );
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <FinanceLayout>
          <div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div>
        </FinanceLayout>
      );
    }
    const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
    return (
      <FinanceLayout>
        <div className="alert alert-red"><div className="alert-body">{msg}</div></div>
      </FinanceLayout>
    );
  }

  const data = q.data!;
  const buckets = data.buckets;
  const bucket180 = buckets.find(b => b.bucket === "180+");
  const overdueShare = data.totals.ar > 0 && bucket180 ? (bucket180.ar / data.totals.ar) * 100 : 0;

  const toggleBucket = (b: ArAgingBucket) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b); else next.add(b);
      return next;
    });
  };

  return (
    <FinanceLayout>
      {/* ── HERO ─────────────────────────────────────────── */}
      <div className="hero-overview" style={{ marginBottom: 20 }}>
        <div>
          <div className="hero-headline">
            {formatRub(data.totals.ar, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Открытая дебиторка по {data.totals.objects_count} {data.totals.objects_count === 1 ? "объекту" : "объектам"}.<br />
            Из {formatRub(data.totals.contract_sum, { unit: true })} контрактов
            получено {formatRub(data.totals.cash_received, { unit: true })}.
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">Просрочка &gt; 180 дней</div>
          <div className="hero-stat-value" style={{ color: bucket180?.ar ? "#FF6B6B" : "#fff" }}>
            {formatRub(bucket180?.ar || 0, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            {bucket180?.objects_count || 0} {bucket180?.objects_count === 1 ? "объект" : "объектов"} ·
            {" "}доля {overdueShare.toFixed(0)}% от всей AR
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">Собираемость</div>
          <div className="hero-stat-value">
            {data.totals.collected_pct.toFixed(1)}<span className="hero-stat-unit">%</span>
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            На дату: {fmtDate(data.as_of)}
            {regionFilter && ` · ${regionFilter}`}
          </div>
        </div>
      </div>

      {/* ── BUCKETS ──────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {buckets.map(b => (
          <BucketKpi
            key={b.bucket}
            b={b}
            totalAr={data.totals.ar}
            isActive={bucketFilter === b.bucket}
            onClick={() => setBucketFilter(bucketFilter === b.bucket ? "" : b.bucket)}
          />
        ))}
      </div>

      {/* ── TOOLBAR ──────────────────────────────────────── */}
      <div className="card card-tight" style={{ marginBottom: 16, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div className="chip-row" style={{ marginBottom: 0 }}>
            <span style={{ fontSize: 12, color: "var(--mute)", marginRight: 4, alignSelf: "center" }}>Регион:</span>
            {(["", "Москва", "Юг"] as const).map(v => (
              <button
                key={v || "all"}
                className={`chip ${regionFilter === v ? "active" : ""}`}
                onClick={() => setRegionFilter(v)}
              >
                {v || "Все"}
              </button>
            ))}
          </div>
          <span style={{ flex: 1 }} />
          {bucketFilter && (
            <button className="chip" onClick={() => setBucketFilter("")}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="x" size={11}/>Бакет: {BUCKET_THEME[bucketFilter].shortLabel}</span>
            </button>
          )}
          <input
            type="text"
            placeholder="Поиск по клиенту или адресу…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              border: "1px solid var(--line)",
              borderRadius: 6,
              fontSize: 13,
              minWidth: 240,
              background: "var(--paper-2)",
              color: "var(--ink)",
              fontFamily: "inherit",
            }}
          />
          <button
            className="chip"
            onClick={() => downloadCsv(`ar-aging-${data.as_of}.csv`, items)}
            title="Скачать таблицу как CSV"
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="download" size={12}/>CSV</span>
          </button>
        </div>
      </div>

      {/* ── ТАБЛИЦА ──────────────────────────────────────── */}
      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 32 }}>
          Нет объектов с непогашенной AR для выбранных фильтров.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
            color: "var(--ink-2)",
          }}>
            <thead>
              <tr style={{
                background: "var(--paper-2)",
                color: "var(--mute)",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontWeight: 500,
              }}>
                <th style={{ textAlign: "left", padding: "10px 14px 10px 16px" }}>Клиент</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>Регион</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>Подписан</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Дней</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Контракт</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Получено</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>AR</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Собрано</th>
                <th style={{ textAlign: "right", padding: "10px 14px" }}>Сделка</th>
              </tr>
            </thead>
            <tbody>
              {BUCKETS_ORDER.map(bk => {
                const rows = grouped.get(bk);
                if (!rows || rows.length === 0) return null;
                const info = buckets.find(b => b.bucket === bk)!;
                const isCollapsed = collapsed.has(bk);
                return (
                  <>
                    <GroupHeader
                      key={`hdr-${bk}`}
                      bucket={bk}
                      info={info}
                      collapsed={isCollapsed}
                      onToggle={() => toggleBucket(bk)}
                    />
                    {!isCollapsed && rows.map(o => (
                      <ObjectRow key={o.object_id} o={o} />
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* hover на строках */}
      <style>{`
        .ar-row > td { padding: 12px 8px; transition: background 100ms ease; }
        .ar-row:hover > td { background: var(--paper-2); }
      `}</style>
    </FinanceLayout>
  );
}
